/**
 * **The right column's day, read as a list** — what the A1 canon draws beside the grid when no
 * session is open: every session of the chosen day in time order, the trainer's blocks among them,
 * and a "now" rule where the day is today.
 *
 * It is the record of the day rather than a picture of it, so it keeps what the grid drops: a
 * session somebody called off stands here, struck through in the destructive ink, exactly as the
 * mobile rail keeps it (`mapSchedule`'s one exception to `occupiesTheDay`). The COUNT does not — it
 * describes what is left of the day, and it has to agree with the blocks on the grid beside it.
 *
 * The status word is a pill beside the name. Most rows carry none: an ordinary session still ahead
 * is what the time column already says. The NEXT session of today carries its countdown while it is
 * under the hour away, which is the canon's `za 20 min`.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS; the component translates.
 */

import { awaitsVerdict, isDrawn, isLiveAt, type BookingSession } from './bookingSession';
import { clockOf, minutesUntil, wallClock, type CivilDay } from './trainerClock';
import { blockSpanOn, type TrainerBlock } from './workingHours';

export type PillTone = 'good' | 'accent' | 'warn' | 'danger' | 'neutral';

export interface StatusPill {
  readonly tone: PillTone;
  readonly labelKey: string;
  readonly params?: Readonly<Record<string, number>>;
  /** A session somebody called off: the word is struck through. */
  readonly strike: boolean;
}

export type AgendaRow =
  | {
      readonly kind: 'session';
      readonly session: BookingSession;
      readonly pill: StatusPill | null;
      /** Today's next session still ahead — the row the canon lifts out of the list. */
      readonly isNext: boolean;
    }
  | {
      readonly kind: 'block';
      readonly block: TrainerBlock;
      readonly startClock: string;
      readonly endClock: string;
    }
  | { readonly kind: 'now'; readonly clock: string };

export interface DayAgenda {
  readonly day: CivilDay;
  readonly isToday: boolean;
  readonly rows: readonly AgendaRow[];
  /** Sessions that hold an hour of the day — the voided ones are listed, never counted. */
  readonly sessionCount: number;
}

interface DayAgendaInput {
  readonly day: CivilDay;
  readonly today: CivilDay;
  readonly sessions: readonly BookingSession[];
  readonly blocks: readonly TrainerBlock[];
  readonly zone: string;
  readonly nowMs: number;
}

/** Under this many minutes away, the next session wears its countdown. */
const COUNTDOWN_MINUTES = 60;

function pillOf(session: BookingSession, nowMs: number, isNext: boolean): StatusPill | null {
  const plain = (tone: PillTone, labelKey: string): StatusPill => ({ tone, labelKey, strike: false });
  if (isLiveAt(session, nowMs)) return plain('accent', 'calendar.status.live');
  switch (session.status) {
    case 'completed':
      return plain('good', 'web:booking.agenda.done');
    case 'missed':
      return plain('warn', 'calendar.status.missed');
    case 'noShow':
      return plain('danger', 'calendar.status.noShow');
    case 'cancelled':
      return { tone: 'danger', labelKey: 'calendar.status.cancelled', strike: true };
    case 'lateCancelled':
      return plain('warn', 'calendar.status.lateCancelled');
    case 'skipped':
      return plain('neutral', 'calendar.status.skipped');
    case 'emptySlot':
      return plain('neutral', 'calendar.sheet.badge.emptySlot');
    case 'live':
    case 'scheduled':
    case null:
      break;
  }
  if (awaitsVerdict(session, nowMs)) return plain('warn', 'calendar.status.consultAwaiting');
  if (isNext && session.hasHour && session.startMs !== null) {
    const minutes = minutesUntil(nowMs, session.startMs);
    if (minutes <= COUNTDOWN_MINUTES) {
      return { tone: 'accent', labelKey: 'web:booking.agenda.startsIn', params: { minutes }, strike: false };
    }
  }
  return null;
}

/** Where a row stands in the day: hourless sessions first (they stand at midnight), then by start. */
function sortKey(row: AgendaRow): number {
  if (row.kind === 'block') return row.block.startMs;
  if (row.kind === 'session') {
    return row.session.hasHour ? (row.session.startMs ?? 0) : Number.NEGATIVE_INFINITY;
  }
  return 0;
}

/** The chosen day as the right column lists it. */
export function dayAgenda({ day, today, sessions, blocks, zone, nowMs }: DayAgendaInput): DayAgenda {
  const ofDay = sessions.filter((session) => session.day === day);
  const next = ofDay
    .filter((session) => !session.voided && session.hasHour && session.startMs !== null && session.startMs > nowMs)
    .sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0))[0];

  const rows: AgendaRow[] = [
    ...ofDay.map(
      (session): AgendaRow => {
        const isNext = day === today && session === next;
        return { kind: 'session', session, pill: pillOf(session, nowMs, isNext), isNext };
      },
    ),
    ...blocks.flatMap((block): AgendaRow[] => {
      const span = blockSpanOn(block, day, zone);
      if (span === null) return [];
      // Clipped to the day: a block running on from yesterday reads from 0:00 here.
      return [{ kind: 'block', block, startClock: clockOf(span.start), endClock: clockOf(span.end) }];
    }),
  ].sort((a, b) => sortKey(a) - sortKey(b));

  const isToday = day === today;
  if (isToday) {
    const at = rows.findIndex(
      (row) =>
        (row.kind === 'session' &&
          row.session.hasHour &&
          row.session.startMs !== null &&
          row.session.startMs >= nowMs) ||
        (row.kind === 'block' && row.block.startMs >= nowMs),
    );
    const marker: AgendaRow = { kind: 'now', clock: wallClock(nowMs, zone) };
    rows.splice(at === -1 ? rows.length : at, 0, marker);
  }

  return {
    day,
    isToday,
    rows,
    // Exactly what the grid draws on this day — `isDrawn` is the one predicate for both.
    sessionCount: ofDay.filter(isDrawn).length,
  };
}
